/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* global localStorage */

const JSG = require('../../JSG');
const Node = require('./Node');
const ItemAttributes = require('../attr/ItemAttributes');
const StringAttribute = require('../attr/StringAttribute');
const FormatAttributes = require('../attr/FormatAttributes');
const GridLayout = require('../../layout/GridLayout');
const NotificationCenter = require('../notifications/NotificationCenter');
const Notification = require('../notifications/Notification');
const StreamSheet = require('./StreamSheet');
const InboxContainer = require('./InboxContainer');
const CaptionNode = require('./CaptionNode');
const LayoutNode = require('./LayoutNode');
const ButtonNode = require('./ButtonNode');
const SplitterNode = require('./SplitterNode');
const StreamSheetModel = require('./StreamSheetModel');

const setupDashboard = (container) => {
	const streamsheet = container.getStreamSheet();
	const layoutNode = new LayoutNode();
	layoutNode.initNew();
	layoutNode.setSize(20000, 10000);
	layoutNode.getPin().setLocalPoint(0, 0);
	layoutNode.setOrigin(0, 0);
	layoutNode.getItemAttributes().setMoveable(JSG.ItemAttributes.Moveable.NONE);
	layoutNode.getItemAttributes().setDeleteable(false);
	streamsheet.getCells().addItem(layoutNode);
	// no header, no grid:

	container.model.sheet.showGrid = false;
	container.model.sheet.showHeader = false;
	return container;
};

/**
 * @class StreamSheetContainer

 * @extends Node
 * @constructor
 */
module.exports = class StreamSheetContainer extends Node {
	static create(settings = {}, graph) {
		const stsContainer = new StreamSheetContainer();
		const {
			id,
			name = '--',
			position = { x: 500, y: 500 },
			sheetId,
			size = { h: 13000, w: 21000 },
			type = 'sheet'
		} = settings;
		stsContainer._parent = graph;
		stsContainer.setId(id);
		stsContainer.evaluate();
		stsContainer.setSize(size.w, size.h);
		stsContainer.setOriginTo(position);
		stsContainer.properties.sheetId = sheetId;
		stsContainer.getStreamSheet().setName(name);
		stsContainer.model.name = name;
		stsContainer.properties.sheetType = type;
		if (type === 'dashboard') setupDashboard(stsContainer);
		return stsContainer;
	}

	constructor() {
		super();

		// left caption
		this._inboxCaption = new CaptionNode();
		this._inboxCaption.setType('inboxcaption');
		this._inboxCaption.setLayout(GridLayout.TYPE);
		this.addItem(this._inboxCaption);

		// inbox container
		this._inboxContainer = new InboxContainer();
		this._inboxContainer.setWidth(5000);
		this.addItem(this._inboxContainer);

		// splitter
		this._splitter = new SplitterNode();
		this._splitter.setDirection(ItemAttributes.Direction.VERTICAL);
		this.addItem(this._splitter);

		this.createButtons();

		this._model = new StreamSheetModel();

		// process sheet
		this._streamSheet = new StreamSheet(this._model._sheet);
		this.addItem(this._streamSheet);

		this.getFormat().setFillColor(JSG.theme.graph);
		this.getFormat().setLineStyle(FormatAttributes.LineStyle.NONE);
		this.getItemAttributes().setPortMode(ItemAttributes.PortMode.NONE);
		this.getItemAttributes().setContainer(false);
		this.getItemAttributes().setClipChildren(true);
		this.getItemAttributes().setSelectionMode(ItemAttributes.SelectionMode.NONE);

		this._activeSheetIndex = 0;
		this._activeSheet = undefined;
		this._viewInfo = {};
		this._inboxCaption.properties  = this._model.properties;
	}

	get model() {
		return this._model;
	}

	set model(model) {
		this._model = model;
	}

	get properties() {
		return this.model.properties;
	}

	get stream() {
		return this.properties.stream;
	}

	set stream(stream) {
		this.properties.stream = stream;
	}

	get sheetSteps() {
		return this.properties.stats.steps;
	}

	set sheetSteps(steps) {
		this.model.properties.stats.steps = steps;
	}

	get stepTimeStamp() {
		return this.properties.stats.ts;
	}

	get stepTimeStampFormatted() {
		const d = new Date(this.properties.stats.ts);
		return `${d.toLocaleDateString(undefined, {
			year: '2-digit',
			month: '2-digit',
			day: '2-digit'
		})} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
	}

	set stepTimeStamp(ts) {
		this.model.properties.stats.ts = ts;
	}

	get activeSheetIndex() {
		const streamsheets = this.getStreamSheetsContainer().streamSheetsModel;
		const index = this._activeSheet ? streamsheets.indexOf(this._activeSheet) : -1;
		return index < 0 ? Math.min(this._activeSheetIndex, streamsheets.length - 1) : index;
	}

	setActiveSheetIndex(index, viewer) {
		const container = this.getStreamSheetsContainer();

		const interaction = viewer.getInteractionHandler().getActiveInteraction();
		if (interaction) {
			interaction.finishInteraction(undefined, viewer);
		}

		viewer.clearSelection();
		index = Math.max(0, index);
		index = Math.min(container.streamSheetsModel.length - 1, index);

		container.modelToStreamSheetItem(this, index, viewer);
		this._activeSheet = container.streamSheetsModel[index];
		this._activeSheetIndex = index;

		NotificationCenter.getInstance().send(
			new Notification(JSG.WorksheetNode.SELECTION_CHANGED_NOTIFICATION, { item: this.getStreamSheet(), updateFinal: true })
		);

		const saveLRU = this._position === 'left' ? 2 : 3;
		let lru = localStorage.getItem(`LRU1`);
		if (lru) {
			lru = lru.split(';');
			if (lru.length > saveLRU) {
				lru[saveLRU] = index;
				lru = lru.join(';');
				localStorage.setItem(`LRU1`, lru);
			}
		}
	}

	get allowSubMarkers() {
		return false;
	}

	createButtons() {
		this._inboxCaption._subItems = [];

		const closeButton = new ButtonNode();
		closeButton.getItemAttributes().addAttribute(new StringAttribute('LayoutHorizontal', 'right'));
		closeButton.getItemAttributes().addAttribute(new StringAttribute('LayoutVertical', 'center'));
		closeButton.getFormat().setFillStyle(FormatAttributes.FillStyle.PATTERN);
		closeButton.getFormat().setPattern('close');
		closeButton.onClick = this.onClick;
		closeButton.setEventScope(this);
		closeButton.setSize(700, 500);
		closeButton.setTooltip(JSG.getLocalizedString('Hide'));
		this._inboxCaption.addItem(closeButton);
		closeButton.setName('closeInbox');
		this._closeButton = closeButton;

	}

	onClick(button) {
		NotificationCenter.getInstance().send(
			new Notification(ButtonNode.BUTTON_CLICKED_NOTIFICATION, { button, container: this })
		);
	}

	newInstance() {
		return new StreamSheetContainer();
	}

	_copy(copiednodes, deep, ids) {
		const copy = super._copy(copiednodes, deep, ids);

		// copy._assignItems();

		return copy;
	}

	_assignName(id) {
		this.setName(`StreamSheetContainer${id}`);
	}

	setMessage(message) {
		this._message = message;
	}

	getMessageIndex() {
		return this._message.getIndex();
	}

	setMessageIndex(index) {
		this._message.setIndex(index);
	}

	getMessage() {
		return this._message;
	}

	get streamSheet() {
		return this._streamSheet;
	}

	getStreamSheet() {
		return this._streamSheet;
	}

	getStreamSheetsContainer() {
		return this.getParent();
	}

	getInboxContainer() {
		return this._inboxContainer;
	}

	getInboxCaption() {
		return this._inboxCaption;
	}

	isAddLabelAllowed() {
		return false;
	}

	getItemType() {
		return 'processsheetcontainer';
	}

	_createId() {
		if (this._parent === undefined) {
			return undefined;
		}

		let id = 1000;

		while (this._parent.getItemById(id)) {
			id += 1000;
		}

		return id;
	}

	layout() {
		const box = JSG.boxCache.get();
		const size = this.getSize().toPoint();
		const sizeInbox = this._inboxContainer.getSize().toPoint();
		let heightCaption = 500;
		let left = 0;
		let inbox;
		let hideButtons = false;
		let captions = true;
		const layoutNode = this._streamSheet.getLayoutNode();
		const margin = 200;

		const settings = this.graph.viewSettings;
		if (this.graph.showViewMode === true) {
			hideButtons = true;
			captions = settings.showInbox;
			heightCaption = captions ? 500 : 0;
			inbox = settings.showInbox;
		} else {
			inbox = this.properties.showInbox;
		}

		this.getInboxContainer()
			.getMessageListItems()
			.setHideEnabledItems(this.properties.hideMessages);

		this._inboxCaption.getItemAttributes().setVisible(inbox);
		this._inboxContainer.getItemAttributes().setVisible(inbox);
		this._inboxContainer.updateSubAttributes();
		this._splitter.getItemAttributes().setVisible(inbox);

		if (captions) {
			this._inboxCaption.subItems.forEach((subItem) => {
				subItem.getItemAttributes().setVisible(hideButtons === false);
			});
		}

		if (inbox) {
			this._closeButton.getItemAttributes().setVisible(!this.model.protected);

			box.setLeft(0);
			box.setTop(0);
			box.setWidth(sizeInbox.x);
			box.setHeight(heightCaption);

			this._inboxCaption.setBoundingBoxTo(box);

			box.setLeft(0);
			box.setTop(heightCaption);
			box.setWidth(sizeInbox.x);
			box.setHeight(size.y - 20);

			this._inboxContainer.setBoundingBoxTo(box);

			box.setTop(0);
			box.setLeft(sizeInbox.x);
			box.setWidth(margin);

			this._splitter.setBoundingBoxTo(box);

			left = sizeInbox.x + margin;
		} else {
			box.setLeft(0);
			box.setTop(heightCaption);
			box.setWidth(sizeInbox.x);
			box.setHeight(size.y);

			this._inboxContainer.setBoundingBoxTo(box);

			box.setTop(0);
			box.setLeft(sizeInbox.x);
			box.setWidth(margin);

			this._splitter.setBoundingBoxTo(box);
		}

		box.setTop(0);
		box.setLeft(left);
		box.setWidth(size.x - left - 20);
		box.setHeight(size.y - 20);

		this._streamSheet.setBoundingBoxTo(box, true);

		if (layoutNode) {
			const layoutMode = layoutNode.getAttributeValueAtPath('layoutmode');
			const minWidth = layoutNode.getAttributeValueAtPath('minwidth');
			const maxWidth = layoutNode.getAttributeValueAtPath('maxwidth');
			let sheetSize = size.x - left;
			const layoutSize = layoutNode.getSizeAsPoint();
			switch (layoutMode) {
				case 'center':
					layoutNode.setOrigin(Math.max(0, (sheetSize - layoutSize.x) / 2), 0);
					break;
				case 'resize': {
					layoutNode.setOrigin(0, 0);
					sheetSize = Math.max(sheetSize, minWidth);
					sheetSize = Math.min(sheetSize, maxWidth);
					const scroll = size.y - heightCaption < layoutSize.y && sheetSize > layoutSize.x;
					if (scroll) {
						sheetSize -= JSG.ScrollBar.SIZE;
					}
					if (sheetSize !== layoutSize.x) {
						layoutNode.setWidth(sheetSize);
						layoutNode.layout();
					}
					const space = size.x - left - (scroll ? JSG.ScrollBar.SIZE : 0);
					if (space > layoutSize.x) {
						layoutNode.setOrigin(Math.max(0, (space - layoutSize.x) / 2), 0);
					}
					break;
				}
				case 'left':
					layoutNode.setOrigin(0, 0);
					break;
			}
		}

		const event = new JSG.Event(JSG.Event.BBOX, box);
		event.source = this;
		this.sendPostEvent(event);

		if (captions) {
			box.setTop(0);
			box.setHeight(heightCaption);

			this._inboxCaption.layout();
		}

		this._inboxContainer.layout();

		JSG.boxCache.release(box);

		super.layout();
	}
};
